/**
 * Supabase client with aggressive no-cache settings for iOS WKWebView compatibility.
 * 
 * This client is used for wallet-related function calls that were getting cached
 * by iOS WebView even with server-side no-cache headers.
 */
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./types";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Custom fetch that prevents caching on iOS WebView without triggering CORS preflights
// (avoid adding non-simple request headers like Cache-Control/Pragma).
const noCacheFetch: typeof fetch = async (input, init) => {
  const cacheBust = `cb=${Date.now()}-${Math.random().toString(36).slice(2)}`;

  // Add cache-bust as a query param (doesn't require CORS allow-header updates)
  const url = typeof input === "string" ? input : input instanceof Request ? input.url : String(input);
  const bustedUrl = url.includes("?") ? `${url}&${cacheBust}` : `${url}?${cacheBust}`;

  const bustedInput = input instanceof Request ? new Request(bustedUrl, input) : bustedUrl;

  return fetch(bustedInput, {
    ...init,
    cache: "no-store", // Critical for iOS WKWebView
  });
};

// Create a separate client instance with no-cache fetch
export const noCacheSupabase: SupabaseClient<Database> = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_KEY,
  {
    global: {
      fetch: noCacheFetch,
    },
    auth: {
      // Don't persist auth in this client - use it only for function calls
      persistSession: false,
      autoRefreshToken: false,
    },
  }
);

/**
 * Helper to invoke edge functions with no-cache settings.
 * Copies auth token from main supabase client session.
 */
export const invokeWithNoCache = async (
  functionName: string,
  body: Record<string, unknown>,
  accessToken?: string
) => {
  const headers: Record<string, string> = {};
  
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  return noCacheSupabase.functions.invoke(functionName, {
    body,
    headers,
  });
};
